import type { EngagementVersion } from '@clockwise/shared';
import { BaseClient } from '../base-client';
import type {
  CopyWeekInput,
  CopyWeekResult,
  CreateTimesheetInput,
  ListTimesheetsQuery,
  PatchTimesheetInput,
  RangeQuery,
  RecallRangeInput,
  SubmitRangeInput,
  Timesheet,
  TimesheetRejection,
} from './types';

type One = { data: Timesheet };
type Many = { data: Timesheet[] };
type ListResponse = {
  data: Timesheet[];
  meta: { total: number; limit: number; offset: number };
};

export interface CustomerInScopeProducts {
  customerId: string;
  productIds: string[];
}
export interface InScopeActivitiesResponse {
  data: CustomerInScopeProducts[];
  meta: {
    fromDate: string;
    toDate: string;
    /** How many of the user's team customers have a covering SOW in the
     *  window. Not `data.length`, which is just the team-customer count. */
    customersWithSOWs: number;
  };
}

/**
 * Engagement coverage for the timesheet pickers, one row per covering SOW
 * VERSION. Version-grained because a week window can span an amendment that
 * changes the activity list — the consumer filters by each entry's own date.
 *
 * `activityIds` EMPTY is meaningful, not missing: an engagement that lists no
 * activities matches every activity.
 */
export interface EngagementsResponse {
  data: EngagementVersion[];
  meta: { fromDate: string; toDate: string };
}

/** Recently-used activities for one customer, product ids ordered
 *  most-recent-first. */
export interface CustomerRecentProducts {
  customerId: string;
  productIds: string[];
}
export interface RecentActivitiesResponse {
  data: CustomerRecentProducts[];
  meta: { fromDate: string; toDate: string };
}

export class TimesheetsClient extends BaseClient {
  list(query: ListTimesheetsQuery = {}): Promise<ListResponse> {
    return this.request('/api/timesheets', { query: normalize(query) });
  }

  /** Single-shot read for week/day views — every entry for a user in [from..to]. */
  range(query: RangeQuery): Promise<Many> {
    return this.request('/api/timesheets/range', { query: normalize(query) });
  }

  /**
   * In-scope activities (SOW retainer product ids) grouped by customer, for
   * the timesheet/timer pickers. The customer set is derived server-side
   * from `userId`'s team memberships over `[fromDate, toDate]` (defaults to
   * the caller), so the request carries only a user + window — no
   * customer-id list to blow past URL limits. One row per team customer,
   * `productIds: []` when no SOW covers the window.
   */
  inScopeActivities(input: {
    userId?: string;
    fromDate: string;
    toDate: string;
  }): Promise<InScopeActivitiesResponse> {
    return this.request('/api/timesheets/in-scope-activities', {
      query: normalize({
        userId: input.userId,
        fromDate: input.fromDate,
        toDate: input.toDate,
      }),
    });
  }

  /**
   * Recently-used activities grouped by customer (most-recent first) — the
   * "used before by you" band of the picker. Same server-derived customer set
   * + window contract as {@link inScopeActivities}; only the customers the
   * user has actually logged against appear.
   */
  recentActivities(input: {
    userId?: string;
    fromDate: string;
    toDate: string;
  }): Promise<RecentActivitiesResponse> {
    return this.request('/api/timesheets/recent-activities', {
      query: normalize({
        userId: input.userId,
        fromDate: input.fromDate,
        toDate: input.toDate,
      }),
    });
  }

  /**
   * Engagements (SOWs) covering `[fromDate, toDate]` for the target user's
   * team customers, with the activity ids each version's retainer lists.
   * Same server-derived customer set + authorization contract as
   * {@link inScopeActivities}.
   */
  engagements(input: {
    userId?: string;
    fromDate: string;
    toDate: string;
  }): Promise<EngagementsResponse> {
    return this.request('/api/timesheets/engagements', {
      query: normalize({
        userId: input.userId,
        fromDate: input.fromDate,
        toDate: input.toDate,
      }),
    });
  }

  get(id: string): Promise<One> {
    return this.request(`/api/timesheets/${encodeURIComponent(id)}`);
  }

  /**
   * Resolution details for a rejected/failed entry — when it was resolved and
   * who rejected it. Fetched lazily by the cell editor only when a rejected
   * cell is opened; `data` is null when the entry isn't rejected/failed or has
   * no approval instance. `rejectedByName` is null for system-driven outcomes
   * (failed / no-match auto-reject), which have no human decider.
   */
  getRejection(id: string): Promise<{ data: TimesheetRejection | null }> {
    return this.request(`/api/timesheets/${encodeURIComponent(id)}/rejection`);
  }

  create(input: CreateTimesheetInput): Promise<One> {
    return this.request('/api/timesheets', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchTimesheetInput): Promise<One> {
    return this.request(`/api/timesheets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/timesheets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  /** Submit a date range (week or day) — flips all draft entries to submitted. */
  submitRange(userId: string, input: SubmitRangeInput): Promise<Many> {
    return this.request(`/api/timesheets/users/${encodeURIComponent(userId)}/submit`, {
      method: 'POST',
      body: input,
    });
  }

  /**
   * Copy the prior week's entries into `weekStart`'s week with hours blanked.
   * The server plans it (dedup against what's already there, skip rows with no
   * hours, skip customers the user has left) and returns the counts, so a
   * caller needs one call rather than a fetch-plan-then-N-writes dance.
   * Re-running it is safe: filled cells are skipped, not duplicated.
   */
  copyWeek(userId: string, input: CopyWeekInput): Promise<{ data: CopyWeekResult }> {
    return this.request(`/api/timesheets/users/${encodeURIComponent(userId)}/copy-week`, {
      method: 'POST',
      body: input,
    });
  }

  /** Recall a date range — flips all submitted entries back to draft and
   *  cancels the in-flight approval instances. */
  recallRange(userId: string, input: RecallRangeInput): Promise<Many> {
    return this.request(`/api/timesheets/users/${encodeURIComponent(userId)}/recall`, {
      method: 'POST',
      body: input,
    });
  }

  /**
   * Recall a single SUBMITTED entry back to draft so its owner can edit and
   * resubmit. Per-entry sibling of {@link recallRange} (which recalls a whole
   * date range). Approved entries can't be recalled by the submitter. Returns
   * the entry, now `draft`.
   */
  recallEntry(id: string): Promise<One> {
    return this.request(`/api/timesheets/${encodeURIComponent(id)}/recall`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Reject an entry the caller previously approved (post-approval
   * correction). Requires `timesheet.approve` and that the caller was the
   * approving decider. The row flip is applied asynchronously, so this
   * returns the entry id, not the updated row.
   */
  rejectApproved(id: string, reason: string): Promise<{ data: { id: string } }> {
    return this.request(`/api/timesheets/${encodeURIComponent(id)}/reject-approved`, {
      method: 'POST',
      body: { reason },
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
